import { AppData, Colors, Lightning } from '@lightningjs/sdk';
import { SpeechType } from '@lightningjs/ui-components';
import Button from 'components/common/buttons/Button';
import MoreInfo from 'components/common/MoreInfo';
import ShowMoreInfoModal from 'components/common/moreInfoModal/ShowMoreInfoModal';
import MediaProgressBar from 'components/common/progressBar/MediaProgressBar';
import {
  formatPlayMedia,
  getUpcomingEpisodeAvailableFormatted,
  isMovie,
} from 'support/contentUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import { translate } from 'support/translate';
import { ShowDetails, ShowVideos, Episode } from 'types/api/media';
import MetadataLabel from 'components/common/MetadataLabel';
import { debounceWithCancel, getImageTextureObj } from 'support/generalUtils';
import { constants } from 'aliases';
import Placeholder from 'components/common/Placeholder';
import ExpandingButton from 'components/common/buttons/ExpandingButton';
import MyStuffToast from 'components/common/toasts/MyStuffToast';
import { WithProgress } from 'services/cwData';
import { ContentBookmarks } from 'types/history';
import { PageId } from 'types/pageId';
import TextLabel from 'components/common/TextLabel';
import { reportMyStuffAddRemove } from 'services/analytics/reportingServicePage';

const INVISIBLE = constants.ui.invisible;
const VISIBLE = 1;

export interface ShowcaseSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  episodes: Array<ShowVideos>;
  extras: ShowVideos | null;
  featuredEpisode: Episode | null;
  show: ShowDetails | null;
  isMyStuffAdded: boolean;
  history: ContentBookmarks;
  pageId: PageId | null;

  Placeholder: typeof Placeholder;
  Background: Lightning.textures.ImageTexture;
  Content: {
    Metadata: typeof MetadataLabel;
    MoreInfo: typeof MoreInfo;
    TextContainer: any;
    ButtonContainer: {
      PlayButtonContainer: {
        PlayButton: typeof Button;
        ProgressBar: typeof MediaProgressBar;
      };
      TrailerButton: typeof ExpandingButton;
      MyStuffButton: typeof ExpandingButton;
    };
  };
  MoreInfoModal: typeof ShowMoreInfoModal;
  MyStuffToast: typeof MyStuffToast;
}

const SHOWCASE_SECTION_WIDTH = 1920;
export const SHOWCASE_SECTION_HEIGHT = 768;

const GRADIENT_PIVOT_X = -0.11;
const GRADIENT_PIVOT_Y = 1.18;

const CONTENT_X = 96;
const CONTENT_Y = 345;

const FONT_SIZE = 29;

const METADATA_PADDING = 5;
const MORE_INFO_PADDING = 20;

const TEXT_CONTAINER_DEFAULT_MARGIN = 85;

const UPCOMING_EPISODE_MARGIN_TOP = 30;
const UPCOMING_EPISODE_MARGIN_BOTTOM = 20;
const UPCOMING_EPISODE_RELEASE_HEIGHT = 34;

const CAST_MARGIN_TOP = 10;
const CAST_MARGIN_BOTTOM = 42;
const CAST_HEIGHT = 34;
const CAST_WIDTH = 674;

const BUTTONS_SPACING = 24;
const PLAY_BUTTON_WIDTH = 400;

const PROGRESS_BAR_MARGIN = 10;

const BUTTON_HEIGHT = 70;
const BUTTON_MIN_WIDTH = 70;
const BUTTON_PADDING_H = 20;

const PLAY_ICON_UNFOCUSED = getImageTextureObj(
  'static/images/playback/play-icon-light.svg',
  18,
  23,
);
const PLAY_ICON_FOCUSED = getImageTextureObj(
  'static/images/playback/play-icon-dark.svg',
  18,
  23,
);

const CLAP_BOARD = getImageTextureObj(
  'static/images/clapboard-white.svg',
  30,
  30,
);
const CLAP_BOARD_FOCUSED = getImageTextureObj(
  'static/images/clapboard-black.svg',
  30,
  30,
);

const MY_STUFF_REMOVED_ICON = getImageTextureObj(
  'static/images/plus-white.svg',
  30,
  30,
);
const MY_STUFF_REMOVED_ICON_FOCUSED = getImageTextureObj(
  'static/images/plus-black.svg',
  30,
  30,
);
const MY_STUFF_ADDED_ICON = getImageTextureObj(
  'static/images/check-white.svg',
  30,
  30,
);
const MY_STUFF_ADDED_ICON_FOCUSED = getImageTextureObj(
  'static/images/x-black.svg',
  30,
  30,
);

export default class ShowcaseSection
  extends Lightning.Component<ShowcaseSectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<ShowcaseSectionTemplateSpec>
{
  private _extras: ShowVideos | null = null;
  private _featuredEpisode: Episode | null = null;
  private _show: ShowDetails | null = null;

  private _Background = this.getByRef('Background')!;
  private _Content = this.getByRef('Content')!;
  private _Metadata = this._Content.getByRef('Metadata')!;
  private _MoreInfo = this._Content.getByRef('MoreInfo')!;
  private _TextContainer = this._Content.getByRef('TextContainer')!;
  private _ButtonContainer = this._Content.getByRef('ButtonContainer')!;
  private _MyStuffToast = this.getByRef('MyStuffToast')!;

  private _PlayButtonContainer = this._ButtonContainer.getByRef(
    'PlayButtonContainer',
  )!;
  private _PlayButton = this._PlayButtonContainer.getByRef('PlayButton')!;
  private _ProgressBar = this._PlayButtonContainer.getByRef('ProgressBar')!;
  private _TrailerButton = this._ButtonContainer.getByRef('TrailerButton')!;
  private _MyStuffButton = this._ButtonContainer.getByRef('MyStuffButton')!;
  private _MoreInfoModal = this.getByRef('MoreInfoModal')!;

  private _isMyStuffAdded = false;
  private _history: ShowcaseSectionTemplateSpec['history'] = {};
  private _pageId: PageId | null = null;

  private _restartWatchlistTimer = debounceWithCancel(() => {
    this.changeWatchlistStatus();
  }, constants.timers.watchlistChangeItemTimeout);

  get announce() {
    if (!this.show) return;

    return [this.show.title, this._Metadata.title, this.show.description];
  }

  get show() {
    return this._show;
  }

  set show(show: ShowDetails | null) {
    this._show = show;
    if (!show) return;

    this.updateBackgroundImage(show);
    this.updateMetadata(show);
    this.updateMoreInfo(show);
    this.updateMoreInfoModal(show);
    this.updateTrailerButton(show);

    if (isMovie(show)) {
      this.setupCast(show);
    }
  }

  set episodes(episodes: Array<ShowVideos>) {
    this.updateUpcomingEpisode(episodes);
  }

  set extras(extras: ShowVideos | null) {
    this._extras = extras;
    this.updatePlayButton();
  }

  set featuredEpisode(featuredEpisode: Episode | null) {
    this._featuredEpisode = featuredEpisode;

    // Order matters since progress is used to defined the play button's title
    this.updateProgressBar();
    this.updatePlayButton();
  }

  set isMyStuffAdded(isMyStuffAdded: boolean) {
    this._isMyStuffAdded = isMyStuffAdded;
    this.setMyStuffButton();
  }

  set history(history: ShowcaseSectionTemplateSpec['history']) {
    this._history = history;
    this.updatePlayButton();
    this.updateProgressBar();
  }

  set pageId(pageId: PageId) {
    this._pageId = pageId;
  }

  get pageId() {
    return this._pageId ?? PageId.UNKNOWN;
  }

  static override _template(): Lightning.Component.Template<ShowcaseSectionTemplateSpec> {
    return {
      w: SHOWCASE_SECTION_WIDTH,
      h: SHOWCASE_SECTION_HEIGHT,
      Placeholder: {
        type: Placeholder,
        w: (w: number) => w,
        h: (h: number) => h,
      },
      Background: {
        w: (w: number) => w,
        h: (h: number) => h,
        shader: {
          type: Lightning.shaders.RadialGradient,
          radius: SHOWCASE_SECTION_HEIGHT,
          pivot: [GRADIENT_PIVOT_X, GRADIENT_PIVOT_Y],
          outerColor: Colors('appBackground').alpha(INVISIBLE).get(),
          innerColor: Colors('appBackground').alpha(VISIBLE).get(),
        },
      },
      Content: {
        w: CAST_WIDTH,
        x: CONTENT_X,
        y: CONTENT_Y,
        flex: { direction: 'column' },
        Metadata: {
          type: MetadataLabel,
          w: CAST_WIDTH,
          shouldCenter: false,
          flexItem: { marginBottom: METADATA_PADDING },
        },
        MoreInfo: {
          x: -MORE_INFO_PADDING,
          type: MoreInfo,
          action: '$showModal',
          signals: {
            $showModal: '$showModal',
            $onHover: '$onHover',
          },
        },
        TextContainer: {
          h: 0, // Adding height 0 so flexbox won't ignore it.
          flexItem: { marginBottom: TEXT_CONTAINER_DEFAULT_MARGIN },
        },
        ButtonContainer: {
          flex: { direction: 'row' },
          PlayButtonContainer: {
            flexItem: { marginRight: BUTTONS_SPACING },
            flex: { direction: 'column' },
            w: PLAY_BUTTON_WIDTH,
            PlayButton: {
              type: Button,
              visible: false,
              minWidth: PLAY_BUTTON_WIDTH,
              height: BUTTON_HEIGHT,
              label: translate('global.play'),
              action: '$playContent',
              endIcon: {
                focused: PLAY_ICON_FOCUSED,
                unfocused: PLAY_ICON_UNFOCUSED,
              },
              passSignals: {
                $playContent: '$playContent',
              },
              signals: {
                $onHover: '$onHover',
              },
            },
            ProgressBar: {
              flexItem: { marginTop: PROGRESS_BAR_MARGIN },
              type: MediaProgressBar,
              alpha: INVISIBLE,
              width: PLAY_BUTTON_WIDTH,
            },
          },
          TrailerButton: {
            flexItem: { marginRight: BUTTONS_SPACING },
            type: ExpandingButton,
            minWidth: BUTTON_MIN_WIDTH,
            paddingH: BUTTON_PADDING_H,
            label: translate('global.playTrailer'),
            action: '$playTrailer',
            startIcon: {
              focused: CLAP_BOARD_FOCUSED,
              unfocused: CLAP_BOARD,
            },
            passSignals: {
              $playTrailer: '$playTrailer',
            },
            signals: {
              $onHover: '$onHover',
            },
          },
          MyStuffButton: {
            flexItem: { marginRight: BUTTONS_SPACING },
            type: ExpandingButton,
            minWidth: BUTTON_MIN_WIDTH,
            paddingH: BUTTON_PADDING_H,
            action: '$addMyStuff',
            signals: {
              $onHover: '$onHover',
              $addMyStuff: '$addMyStuff',
            },
          },
        },
      },
      MoreInfoModal: {
        type: ShowMoreInfoModal,
        visible: false,
      },
      MyStuffToast: {
        type: MyStuffToast,
      },
    };
  }

  override _active() {
    this.setMyStuffButton();
    this.updateInitialState();
  }

  override _inactive() {
    this._TextContainer.patch({ alpha: INVISIBLE });
    this._PlayButton.patch({ visible: false });
    this._TrailerButton.patch({ visible: false });
    this._Background.patch({ texture: undefined });
    this._restartWatchlistTimer.cancel();
    if (this._MoreInfoModal.visible) this.hideModal();
  }

  private updateInitialState() {
    if (this._PlayButton.visible) {
      this._setState('PlayButton');
    } else if (this._TrailerButton.visible) {
      this._setState('TrailerButton');
    } else {
      this._setState('MoreInfo');
    }
  }

  private updateBackgroundImage(show: ShowDetails) {
    // Reset background texture
    this._Background.patch({ texture: undefined });

    const src = show.images.image_featured ?? '';
    const backgroundTexture = getImageTextureObj(
      src,
      SHOWCASE_SECTION_WIDTH,
      SHOWCASE_SECTION_HEIGHT,
    );
    this._Background.patch(backgroundTexture);
  }

  private updateMetadata(show: ShowDetails) {
    this._Metadata.patch({ mediaContent: show });
  }

  private updateMoreInfo(show: ShowDetails) {
    const { description } = show;
    this._MoreInfo.patch({ description });
  }

  private updateMoreInfoModal(show: ShowDetails) {
    this._MoreInfoModal.patch({ mediaContent: show });
  }

  private updateTrailerButton(show: ShowDetails) {
    const { featuredTrailer } = show;
    const hasTrailer = !!featuredTrailer?.id;
    this._TrailerButton.patch({ visible: hasTrailer });
    if (hasTrailer && !this._TrailerButton.hasFocus()) {
      this._TrailerButton._unfocus();
    }
  }

  private updateUpcomingEpisode(episodes: Array<ShowVideos>) {
    if (!episodes.length) return;

    const lastEpisodesList = episodes[episodes.length - 1]!;
    if (!lastEpisodesList.nextEpisodes.length) return;

    const { caption, date } = lastEpisodesList.nextEpisodes[0]!;
    if (!caption || !date) return;

    const captionLabel = translate(
      'seriesDetail.upcomingEpisodeCaption',
      caption,
    );
    const dateLabel = getUpcomingEpisodeAvailableFormatted(date);

    this._TextContainer.patch({
      alpha: VISIBLE,
      flexItem: {
        marginTop: UPCOMING_EPISODE_MARGIN_TOP,
        marginBottom: UPCOMING_EPISODE_MARGIN_BOTTOM,
      },
      flex: { direction: 'row' },
      h: UPCOMING_EPISODE_RELEASE_HEIGHT,
      UpcomingCaptionLabel: {
        type: TextLabel,
        text: {
          fontFace: getFontFaceFromStyle('light'),
          fontSize: FONT_SIZE,
          text: captionLabel,
        },
      },
      UpcomingEpisodeDate: {
        type: TextLabel,
        text: {
          fontFace: getFontFaceFromStyle('bold'),
          fontSize: FONT_SIZE,
          text: dateLabel,
        },
      },
    });
  }

  private updateProgressBar() {
    const featuredItem = this._featuredEpisode ?? undefined;
    const history = featuredItem ? this._history[featuredItem.guid] : undefined;

    let mediaItem: WithProgress<Episode> | undefined;
    if (featuredItem && history !== undefined) {
      const { progress, isComplete } = history;
      mediaItem = { ...featuredItem, progress, isComplete };
    }

    this._ProgressBar.patch({ mediaItem });

    const alpha = this._ProgressBar.progressPercent > 0 ? VISIBLE : INVISIBLE;
    this._ProgressBar.patch({ alpha });
  }

  private updatePlayButton() {
    const extras = this._extras;
    const featuredEpisode = this._featuredEpisode;
    const show = this._show;

    let label = '';
    let title: SpeechType = '';

    if (featuredEpisode && show) {
      const { guid } = featuredEpisode;
      const videoHistory = this._history[guid];

      const featuredContent = isMovie(show) ? show : featuredEpisode;

      label = formatPlayMedia(featuredContent, {
        progress: videoHistory?.progress,
        isComplete: videoHistory?.isComplete,
      });

      const progress =
        this._ProgressBar.alpha === VISIBLE ? this._ProgressBar.title : '';
      title = [label, progress];
    } else if (extras && extras.items.length) {
      const playNowText = translate('global.playNow');
      label = playNowText;
      title = playNowText;
    } else {
      // No content, can't update play button
      return;
    }

    this._PlayButton.patch({ visible: true, label, title });
    this.updateInitialState();
  }

  private setupCast(show: ShowDetails) {
    const cast = show.showCast ?? '';
    if (!cast) return;

    const castText = translate('global.castWithNames', cast);
    this._TextContainer.patch({
      alpha: VISIBLE,
      flexItem: {
        marginTop: CAST_MARGIN_TOP,
        marginBottom: CAST_MARGIN_BOTTOM,
      },
      flex: { direction: 'row' },
      h: CAST_HEIGHT,
      Cast: {
        w: CAST_WIDTH,
        text: {
          fontFace: getFontFaceFromStyle('light'),
          fontSize: FONT_SIZE,
          text: castText,
          maxLines: 1,
        },
      },
    });
  }

  private hideModal() {
    this._MoreInfoModal.visible = false;
    this.signal('$modalToggled', { visible: false });
  }

  private setMyStuffButton() {
    const isLoggedIn = !!AppData?.loginRadiusService?.isLoggedIn();
    this._MyStuffButton.patch({ visible: isLoggedIn });

    if (isLoggedIn && this._show) {
      this.toggleMyStuffButton(this._isMyStuffAdded);
    }
  }

  private $addMyStuff() {
    if (!this._MyStuffButton.visible || !this._show) return;

    // Toggle state and update icon/label
    this._isMyStuffAdded = !this._isMyStuffAdded;
    this.toggleMyStuffButton(this._isMyStuffAdded);

    this._restartWatchlistTimer.debounce();
  }

  private changeWatchlistStatus() {
    if (!this._MyStuffButton.visible || !this._show) return;

    const seriesName = this.show?.title ?? '';

    this._isMyStuffAdded
      ? AppData?.personaService?.addToWatchlist(this._show)
      : AppData?.personaService?.removeFromWatchlist([this._show.id]);

    reportMyStuffAddRemove({
      actionType: this._isMyStuffAdded ? 'Add' : 'Remove',
      type: seriesName,
      pageId: this.pageId,
      rowName: 'Watchlist',
    });

    this._MyStuffToast.updateMessage(
      this._isMyStuffAdded,
      this.show?.title ?? '',
    );
  }

  private toggleMyStuffButton(isAdded: boolean) {
    this._MyStuffButton.patch({
      startIcon: {
        focused: isAdded
          ? MY_STUFF_ADDED_ICON_FOCUSED
          : MY_STUFF_REMOVED_ICON_FOCUSED,
        unfocused: isAdded ? MY_STUFF_ADDED_ICON : MY_STUFF_REMOVED_ICON,
      },
      label: isAdded ? translate('global.remove') : translate('myStuff.title'),
    });
    this._MyStuffButton.updateButtonWidth();
  }

  $showModal() {
    this._MoreInfoModal.visible = true;
    this._setState('MoreInfoModal');

    this.signal('$modalToggled', { visible: true });
  }

  $onHover(target: any) {
    switch (target) {
      case this._PlayButton:
        this._setState('PlayButton');
        break;
      case this._TrailerButton:
        this._setState('TrailerButton');
        break;
      case this._MyStuffButton:
        this._setState('MyStuffButton');
        break;
      case this._MoreInfo:
        this._setState('MoreInfo');
        break;
    }
    this.signal('$onShowcaseHover');
  }

  static override _states() {
    return [
      class MoreInfo extends this {
        override _getFocused() {
          return this._MoreInfo;
        }

        override _handleDown() {
          if (this._PlayButton.visible) {
            this._setState('PlayButton');
          } else if (this._TrailerButton.visible) {
            this._setState('TrailerButton');
          } else {
            return false;
          }
        }
      },

      class MoreInfoModal extends this {
        override _getFocused() {
          return this._MoreInfoModal;
        }

        override _handleBack() {
          this._setState('MoreInfo');
          this.hideModal();
        }

        override _handleKey() {
          return;
        }
      },
      class PlayButton extends this {
        override _getFocused() {
          return this._PlayButton;
        }

        override _handleUp() {
          this._setState('MoreInfo');
        }

        override _handleRight() {
          if (this._TrailerButton.visible) {
            this._setState('TrailerButton');
          } else if (this._MyStuffButton.visible) {
            this._setState('MyStuffButton');
          }
        }
      },
      class TrailerButton extends this {
        override _getFocused() {
          return this._TrailerButton;
        }

        override _handleUp() {
          this._setState('MoreInfo');
        }

        override _handleLeft() {
          if (this._PlayButton.visible) {
            this._setState('PlayButton');
          }
        }
        override _handleRight() {
          if (this._MyStuffButton.visible) {
            this._setState('MyStuffButton');
          }
        }
      },
      class MyStuffButton extends this {
        override _getFocused() {
          return this._MyStuffButton;
        }

        override _handleLeft() {
          if (this._TrailerButton.visible) {
            this._setState('TrailerButton');
          } else if (this._PlayButton.visible) {
            this._setState('PlayButton');
          }
        }

        override _handleUp() {
          this._setState('MoreInfo');
        }
      },
    ];
  }
}
